import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageTitle from "./include/PageTitle";
import Loader from "../Components/Loader";
import { useI18n } from "../i18n/I18nContext";
import { useTranslatedCopy } from "../i18n/useTranslatedCopy";
import { API_BASE, fetchApiJson } from "../config/apiBase";
import Seo, { mergeSeoKeywords } from "../Components/Seo";

const fallbackCover = "/img/blog/1.jpg";

const resolveImage = (url) => {
  if (!url) return fallbackCover;
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
    return `${API_BASE}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const handleImageFallback = (event) => {
  if (event.currentTarget.src.includes(fallbackCover)) return;
  event.currentTarget.src = fallbackCover;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const Blogs = () => {
  const { locale } = useI18n();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let ignore = false;

    const loadBlogs = async () => {
      try {
        setLoading(true);
        setError("");
        const { json } = await fetchApiJson(`/api/blogs?lang=${locale}`);
        if (!ignore) {
          setBlogs(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || "Failed to load blogs");
          setBlogs([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadBlogs();
    return () => {
      ignore = true;
    };
  }, [locale]);

  const ui = useTranslatedCopy(
    {
      loading: "Loading blogs...",
      noBlogs: "No published blogs yet.",
      readMore: "Read more",
      by: "by",
      admin: "Admin",
      news: "News",
      previousPage: "Previous page",
      nextPage: "Next page",
    },
    locale,
    "blogs-ui"
  );

  const blogsPerPage = 9;
  const totalPages = Math.max(1, Math.ceil(blogs.length / blogsPerPage));
  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * blogsPerPage;
    return blogs.slice(startIndex, startIndex + blogsPerPage);
  }, [blogs, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let page = 1; page <= totalPages; page += 1) {
      pages.push(page);
    }

    return (
      <div className="row">
        <div className="col-lg-12">
          <div className="ltn__pagination-area text-center">
            <div className="ltn__pagination">
              <ul>
                <li>
                  <a
                    href="#!"
                    aria-label={ui.previousPage}
                    aria-disabled={currentPage === 1}
                    onClick={(event) => {
                      event.preventDefault();
                      if (currentPage !== 1) goToPage(currentPage - 1);
                    }}
                  >
                    <i className="fas fa-angle-double-left" />
                  </a>
                </li>
                {pages.map((page) => (
                  <li key={page} className={page === currentPage ? "active" : ""}>
                    <a
                      href="#!"
                      onClick={(event) => {
                        event.preventDefault();
                        goToPage(page);
                      }}
                    >
                      {page}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#!"
                    aria-label={ui.nextPage}
                    aria-disabled={currentPage === totalPages}
                    onClick={(event) => {
                      event.preventDefault();
                      if (currentPage !== totalPages) goToPage(currentPage + 1);
                    }}
                  >
                    <i className="fas fa-angle-double-right" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Seo
        title="Saudi Real Estate Insights & News | QTECH"
        titleAr="أخبار ورؤى السوق العقاري السعودي | شركة تقنيات الجودة"
        description="Read QTECH real estate insights, property market updates, and practical guidance for property management, brokerage, marketing, maintenance, and development in Saudi Arabia."
        descriptionAr="اقرأ أخبار ورؤى شركة تقنيات الجودة حول السوق العقاري السعودي، إدارة الأملاك، التسويق العقاري، الوساطة العقارية، صيانة العقارات، والتطوير العقاري."
        keywords={mergeSeoKeywords(
          "Saudi real estate news, Saudi property market, real estate insights Saudi Arabia, QTECH blog",
          "real estate investment Saudi Arabia, property investment insights Saudi Arabia, Saudi real estate investment news, investment property market Saudi Arabia"
        )}
        keywordsAr={mergeSeoKeywords(
          "أخبار عقارية سعودية، السوق العقاري السعودي، عقارات الشرقية، عقارات الخبر، استثمار عقاري، تسويق عقاري، إدارة أملاك، وساطة عقارية، شركة تقنيات الجودة",
          "استثمار عقاري في السعودية، أخبار الاستثمار العقاري، فرص استثمار عقاري، السوق العقاري الاستثماري"
        )}
        path="/blogs"
        image="/img/blog/1.jpg"
      />

      <PageTitle titleKey="pages.news.title" currentPageKey="pages.news.title" bgImage="/img/bg/14.jpg" />

      <div className="ltn__blog-area ltn__blog-item-3-normal mb-100">
        <div className="container">
          {loading ? (
            <div className="row">
              <div className="col-12 text-center">
                <Loader label={ui.loading} />
              </div>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="row">
              <div className="col-12 text-center">
                <p className="text-danger">{error}</p>
              </div>
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <div className="row">
                {blogs.length === 0 ? (
                  <div className="col-12 text-center">
                    <p>{ui.noBlogs}</p>
                  </div>
                ) : null}

                {paginatedBlogs.map((blog) => {
                  const primaryTag = blog.tags?.[0]?.name || ui.news;

                  return (
                    <div className="col-lg-4 col-sm-6 col-12" key={blog.id || blog.slug}>
                      <div className="ltn__blog-item ltn__blog-item-3">
                        <div className="ltn__blog-img">
                          <Link to={`/blogs-details/${blog.slug}`}>
                            <img
                              className="qtech-blog-card-image"
                              src={resolveImage(blog.coverImageUrl)}
                              alt={blog.title || "QTECH real estate blog"}
                              onError={handleImageFallback}
                            />
                          </Link>
                        </div>
                        <div className="ltn__blog-brief">
                          <div className="ltn__blog-meta">
                            <ul>
                              <li className="ltn__blog-author">
                                <a href="#!" onClick={(event) => event.preventDefault()}>
                                  <i className="far fa-user" />
                                  {ui.by}: {blog.author?.fullName || ui.admin}
                                </a>
                              </li>
                              <li className="ltn__blog-tags">
                                <a href="#!" onClick={(event) => event.preventDefault()}>
                                  <i className="fas fa-tags" />
                                  {primaryTag}
                                </a>
                              </li>
                            </ul>
                          </div>
                          <h3 className="ltn__blog-title animated fadeIn">
                            <Link to={`/blogs-details/${blog.slug}`}>{blog.title}</Link>
                          </h3>
                          <div className="ltn__blog-meta-btn">
                            <div className="ltn__blog-meta">
                              <ul>
                                <li className="ltn__blog-date">
                                  <i className="far fa-calendar-alt" />
                                  {formatDate(blog.publishedAt)}
                                </li>
                              </ul>
                            </div>
                            <div className="ltn__blog-btn">
                              <Link to={`/blogs-details/${blog.slug}`}>{ui.readMore}</Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderPagination()}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Blogs;
